import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemButtonProps,
    PopperPlacementType,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { CSSProperties, ReactNode } from "react";
import HPopper from "../Popper/Popper";
import { FollowTooltip } from "../Tooltip";

export interface IListItem {
    text?: string;
    icon?: string;
    onClick?: ({ togglePopper }: { togglePopper: (open: boolean) => void }) => void;
    children?: ReactNode;
    hoverItems?: IListItem[];
    hoverItemsContainerStyle?: CSSProperties;
    props?: ListItemButtonProps & { "data-testid"?: string };
    title?: string;
    /** Renders a title above each section category. Include the logic to show the title in sectionTitleShow */
    sectionTitle?: string;
    /** Controls when to show the section title */
    sectionTitleShow?: boolean;
}

interface IListPopper {
    placement?: PopperPlacementType;
    listItems: IListItem[];
    listFooter?: ReactNode;
    listTitle?: ReactNode;
    children: ReactNode;
    anchorStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    listStyle?: CSSProperties;
    dontCloseOnClick?: boolean;
    fitToContainer?: boolean;
    onClick?: () => void;
    onClickOverride?: Function;
    showAction?: "click" | "hover";
    disabled?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const ListPopper = ({
    placement,
    listItems,
    listFooter,
    listTitle,
    children,
    anchorStyle,
    listStyle,
    dontCloseOnClick,
    fitToContainer,
    onClickOverride,
    containerStyle,
    onClick,
    showAction = "click",
    disabled = false,
    onOpenChange,
}: IListPopper) => {
    if (disabled) {
        return <div style={{ width: "fit-content", ...anchorStyle }}>{children}</div>;
    }

    return (
        <HPopper
            showAction={showAction}
            placement={placement}
            anchorStyle={anchorStyle}
            onClick={onClick}
            onClickOverride={onClickOverride}
            containerStyle={containerStyle}
            onOpenChange={onOpenChange}
            Content={({ anchorRef, togglePopper }) => (
                <>
                    <List
                        subheader={listTitle}
                        style={{
                            ...(fitToContainer && { width: anchorRef.current?.clientWidth }),
                            ...listStyle,
                        }}
                    >
                        {listItems.map((item, i) => (
                            <ListItem disablePadding key={item.text || i}>
                                {!item.hoverItems ? (
                                    <Item
                                        item={item}
                                        togglePopper={togglePopper}
                                        dontCloseOnClick={dontCloseOnClick}
                                    />
                                ) : (
                                    <HPopper
                                        placement="right-start"
                                        anchorStyle={{ width: "100%" }}
                                        containerStyle={{ margin: 0, ...item.hoverItemsContainerStyle }}
                                        showAction="hover"
                                        Content={() => (
                                            <List style={{ padding: 0 }}>
                                                {item.hoverItems?.map((hoverItem, j) => (
                                                    <ListItem disablePadding key={hoverItem.text || j}>
                                                        <Item
                                                            dontCloseOnClick={dontCloseOnClick}
                                                            togglePopper={togglePopper}
                                                            item={hoverItem}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    >
                                        <Item
                                            item={item}
                                            togglePopper={togglePopper}
                                            chevron
                                            dontCloseOnClick
                                        />
                                    </HPopper>
                                )}
                            </ListItem>
                        ))}
                    </List>
                    {listFooter && (
                        <>
                            <Divider sx={{ borderColor: (theme) => theme.palette.extendedGreen2.main }} />
                            {listFooter}
                        </>
                    )}
                </>
            )}
        >
            {children}
        </HPopper>
    );
};
const Item = ({
    item,
    togglePopper,
    dontCloseOnClick,
    chevron,
}: {
    item: IListItem;
    togglePopper: (open: boolean) => void;
    dontCloseOnClick?: boolean;
    chevron?: boolean;
}) => {
    const theme = useTheme();
    return (
        <FollowTooltip title={item.title || ""} style={{ width: "100%" }}>
            {item.sectionTitleShow && (
                <Typography
                    variant="caption"
                    color={theme.palette.extendedGrey1.main}
                    style={{ padding: "0.5rem 1rem" }}
                    component={"p"}
                >
                    {item.sectionTitle}
                </Typography>
            )}
            <ListItemButton
                sx={{
                    minHeight: "48px",
                    padding: "8px 16px",
                    borderRadius: "2px",
                    width: "100%",
                    cursor: item.onClick ? "pointer" : "auto",
                }}
                onClick={() => {
                    item.onClick?.({
                        togglePopper,
                    });
                    !dontCloseOnClick && togglePopper(false);
                }}
                {...item.props}
            >
                <Typography component="div" variant="body2" style={{ width: "100%" }}>
                    <Stack direction="row" spacing={2}>
                        {item.icon && (
                            <img
                                alt={item.text}
                                src={item.icon}
                                role="button"
                                height="24px"
                                width="24px"
                                style={{
                                    borderRadius: "2px",
                                    cursor: "pointer",
                                }}
                            />
                        )}
                        {item.text}
                        {item.children}
                    </Stack>
                </Typography>
                {chevron && <ChevronRightRoundedIcon style={{ fontSize: "1.5rem" }} />}
            </ListItemButton>
        </FollowTooltip>
    );
};

export default ListPopper;
